import { useRef, useState } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

import {
  NotificationServiceContextProvider,
  useNotificationServiceContext,
} from '@/core/notifications/contexts/notification-service-context';
import { Subscription } from '@/core/notifications/model';
import { subscriptionCoversFilter } from '@/core/notifications/model/subscription-coverage';
import { Loading } from '@/shared/components/export';
import SubscriptionWizardBody, {
  SubscriptionWizardHandle,
} from '@/core/notifications/components/subscription-wizard/subscription-wizard-body';
import SubscribeManageView from './subscribe-manage-view';
import CoverageMismatchList from './coverage-mismatch-list';

const CREATE_LABEL = 'Create alarm';
const EDIT_LABEL = 'Edit alarm';
const ALREADY_NOTIFIED_LABEL = 'You are already being notified about this';

function TopicSubscribeButtonInner({
  topicKey,
  tagFilter,
  suggestedName,
  showLabel,
}: {
  topicKey: string;
  tagFilter: Record<string, unknown>;
  suggestedName: string;
  showLabel?: boolean;
}) {
  const { topics, channels, subscriptions, refreshSubscriptions, isLoading } =
    useNotificationServiceContext();
  const [open, setOpen] = useState(false);
  const [forceCreate, setForceCreate] = useState(false);
  const [manageId, setManageId] = useState<string | null>(null);
  const wizardRef = useRef<SubscriptionWizardHandle>(null);

  const topic = topics.find(t => t.key === topicKey);
  const topicSubscriptions = subscriptions.filter(s => s.topic === topicKey);
  const covering = topicSubscriptions.filter(s =>
    subscriptionCoversFilter(topic, s.tagFilter, tagFilter),
  );

  const close = () => {
    setOpen(false);
    setForceCreate(false);
    setManageId(null);
  };

  const handleSaved = () => {
    refreshSubscriptions();
    close();
  };

  const renderBody = () => {
    if (isLoading) return <Loading type="bar" />;
    if (!topic) return <div>Unknown notification topic.</div>;

    if (forceCreate || topicSubscriptions.length === 0) {
      return (
        <SubscriptionWizardBody
          ref={wizardRef}
          topics={[topic]}
          applications={[]}
          channels={channels}
          preload={{ topicKey, tagFilter, suggestedName }}
          onDone={() => handleSaved()}
          onCancel={close}
        />
      );
    }

    if (covering.length > 0) {
      const managing: Subscription = covering.find(s => s.id === manageId) ?? covering[0];

      if (covering.length > 1 && !manageId) {
        return (
          <div>
            <div className={`mb-2 mutedText`} style={{ fontSize: 13 }}>
              Multiple alarms cover what you're viewing — pick one to manage.
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {covering.map(sub => (
                <button
                  key={sub.id}
                  className="btn btn-sm btn-outline-secondary d-block mb-2"
                  onClick={() => setManageId(sub.id)}
                >
                  {sub.name}
                  {sub.paused && <span className="badge bg-secondary ms-2">Paused</span>}
                </button>
              ))}
            </div>
          </div>
        );
      }

      return (
        <SubscribeManageView
          subscription={managing}
          topic={topic}
          channels={channels}
          onUpdated={handleSaved}
          onCancel={close}
          onBack={covering.length > 1 ? () => setManageId(null) : undefined}
        />
      );
    }

    return (
      <CoverageMismatchList
        topic={topic}
        subscriptions={topicSubscriptions}
        tagFilter={tagFilter}
        onExpanded={handleSaved}
        onCreateNew={() => setForceCreate(true)}
      />
    );
  };

  const covered = covering.length > 0;

  const label = covered ? EDIT_LABEL : CREATE_LABEL;

  const accessibleName = covered ? `${label} — ${ALREADY_NOTIFIED_LABEL}` : label;

  return (
    <>
      <OverlayTrigger
        placement="top"
        overlay={covered ? <Tooltip>{ALREADY_NOTIFIED_LABEL}</Tooltip> : <></>}
      >
        <span className="d-inline-block" data-testid="topic-subscribe-trigger">
          <button
            type="button"
            className="btn btn-sm btn-secondary d-inline-flex align-items-center gap-1"
            aria-label={accessibleName}
            disabled={isLoading}
            onClick={() => setOpen(true)}
          >
            <FontAwesomeIcon icon={faBell} />
            {showLabel && <span>{label}</span>}
          </button>
        </span>
      </OverlayTrigger>

      <Modal
        show={open}
        onHide={() => (wizardRef.current ? wizardRef.current.requestCancel() : close())}
        centered
        className="notifications-settings"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 14, fontWeight: 700 }}>Notifications</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderBody()}</Modal.Body>
      </Modal>
    </>
  );
}

export default function TopicSubscribeButton(props: {
  topicKey: string;
  tagFilter: Record<string, unknown>;
  suggestedName: string;
  showLabel?: boolean;
}) {
  return (
    <NotificationServiceContextProvider>
      <TopicSubscribeButtonInner {...props} />
    </NotificationServiceContextProvider>
  );
}
